import React from 'react';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import {
  array,
  arrayOf,
  bool,
  func,
  node,
  number,
  object,
  oneOfType,
  shape,
  string,
} from 'prop-types';
import loadable from '@loadable/component';
import classNames from 'classnames';

import { intlShape, injectIntl, FormattedMessage } from '../../util/reactIntl';
import { displayPrice } from '../../util/configHelpers';
import {
  propTypes,
  LISTING_STATE_CLOSED,
  LINE_ITEM_NIGHT,
  LINE_ITEM_DAY,
  LINE_ITEM_ITEM,
  LINE_ITEM_HOUR,
} from '../../util/types';
import { formatMoney } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';
import { parse, stringify } from '../../util/urlHelpers';
import {
  INQUIRY_PROCESS_NAME,
  getSupportedProcessesInfo,
  isBookingProcess,
  isPurchaseProcess,
  resolveLatestProcessName,
} from '../../transactions/transaction';

import { PrimaryButton, H2, AvatarMedium } from '../../components';

import css from './OrderPanel.module.css';
import { capitalize } from '../../util/genericHelpers';
import { conditionLabel } from '../../util/listingCondition';
import getCountryCodes from '../../translations/countryCodes';
import { EN } from '../../util/constants';
import { getSellerVacation } from '../../util/vacation';

const { Money } = sdkTypes;

const BookingTimeForm = loadable(() =>
  import(/* webpackChunkName: "BookingTimeForm" */ './BookingTimeForm/BookingTimeForm')
);
const BookingDatesForm = loadable(() =>
  import(/* webpackChunkName: "BookingDatesForm" */ './BookingDatesForm/BookingDatesForm')
);
const InquiryWithoutPaymentForm = loadable(() =>
  import(
    /* webpackChunkName: "InquiryWithoutPaymentForm" */ './InquiryWithoutPaymentForm/InquiryWithoutPaymentForm'
  )
);
const ProductOrderForm = loadable(() =>
  import(/* webpackChunkName: "ProductOrderForm" */ './ProductOrderForm/ProductOrderForm')
);

const TODAY = new Date();

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: `(${price.currency})`,
      priceTitle: `Unsupported currency (${price.currency})`,
    };
  }
  return {};
};

const openOrderModal = (isOwnListing, isClosed, history, location) => {
  if (isOwnListing || isClosed) {
    window.scrollTo(0, 0);
  } else {
    const { pathname, search, state } = location;
    const searchString = `?${stringify({ ...parse(search), orderOpen: true })}`;
    history.push(`${pathname}${searchString}`, state);
  }
};

const handleSubmit = (
  isOwnListing,
  isClosed,
  isInquiryWithoutPayment,
  onSubmit,
  history,
  location
) => {
  // TODO: currently, inquiry-process does not have any form to ask more order data.
  // We can submit without opening any inquiry/order modal.
  return isInquiryWithoutPayment
    ? () => onSubmit({})
    : () => openOrderModal(isOwnListing, isClosed, history, location);
};

const dateFormattingOptions = { month: 'short', day: 'numeric', weekday: 'short' };

const PriceMaybe = props => {
  const { price, publicData, validListingTypes, intl } = props;
  const { listingType, unitType } = publicData || {};

  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showPrice = displayPrice(foundListingTypeConfig);
  if (!showPrice || !price) {
    return null;
  }

  // Surface flat-rate / free-shipping cost up-front so buyers don't have
  // to reach checkout to learn the shipping price. Calculated shipping
  // depends on the buyer's address and is still resolved at checkout.
  const domesticParams = publicData?.domesticParams;
  const isFreeShipping = !!domesticParams?.isFreeShipping;
  const isFlatRate = domesticParams?.domesticCostType === 'flat-rate';
  const flatRateDollars = Number(domesticParams?.ownShippingPrice) || 0;
  const currency = price?.currency || 'USD';
  // A listing the buyer can only collect has no shipping price to show.
  const deliveryMethods = Array.isArray(publicData?.deliveryMethods)
    ? publicData.deliveryMethods
    : [];
  const pickupOnly = deliveryMethods.length > 0 && !deliveryMethods.includes('shipping');
  const shippingNote = pickupOnly ? (
    <FormattedMessage id="OrderPanel.pickupOnly" />
  ) : isFreeShipping ? (
    <FormattedMessage id="OrderPanel.freeShipping" />
  ) : isFlatRate && flatRateDollars > 0 ? (
    <FormattedMessage
      id="OrderPanel.flatRateShipping"
      values={{ price: formatMoney(intl, new Money(Math.round(flatRateDollars * 100), currency)) }}
    />
  ) : null;
  // How soon it ships is in the facts below the buttons (ListingPage)
  return (
    <div className={css.priceContainer}>
      <div className={css.priceRow}>
        <p className={css.price}>{formatMoney(intl, price)}</p>
        {shippingNote ? <span className={css.shippingNote}>{shippingNote}</span> : null}
      </div>
      <div className={css.perUnit}>
        <FormattedMessage id="OrderPanel.perUnit" values={{ unitType }} />
      </div>
      <div className={css.taxNotice}>
        <FormattedMessage id="OrderPanel.taxNotice" />
      </div>
    </div>
  );
};

const OrderPanel = props => {
  const {
    rootClassName,
    className,
    titleClassName,
    listing,
    validListingTypes,
    lineItemUnitType: lineItemUnitTypeMaybe,
    isOwnListing,
    onSubmit,
    title,
    author,
    authorLink,
    onFetchTimeSlots,
    monthlyTimeSlots,
    setIsSocialSharingModalOpen = () => {},
    onOpenOffersModal = () => {},
    history,
    location,
    intl,
    onFetchTransactionLineItems,
    onContactUser,
    lineItems,
    marketplaceCurrency,
    dayCountAvailableForBooking,
    marketplaceName,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    deliveryMethod,
    editListingParams,
  } = props;

  const publicData = listing?.attributes?.publicData || {};
  const { unitType, transactionProcessAlias = '', condition, conditionDescription } =
    publicData || {};
  const processName = resolveLatestProcessName(transactionProcessAlias.split('/')[0]);
  const lineItemUnitType = lineItemUnitTypeMaybe || `line-item/${unitType}`;

  const price = listing?.attributes?.price;
  const isPaymentProcess = processName !== INQUIRY_PROCESS_NAME;

  const showPriceMissing = isPaymentProcess && !price;
  const PriceMissing = () => {
    return (
      <p className={css.error}>
        <FormattedMessage id="OrderPanel.listingPriceMissing" />
      </p>
    );
  };
  const showInvalidCurrency = isPaymentProcess && price?.currency !== marketplaceCurrency;
  const InvalidCurrency = () => {
    return (
      <p className={css.error}>
        <FormattedMessage id="OrderPanel.listingCurrencyInvalid" />
      </p>
    );
  };

  const timeZone = listing?.attributes?.availabilityPlan?.timezone;
  const isClosed = listing?.attributes?.state === LISTING_STATE_CLOSED;

  const isBooking = isBookingProcess(processName);
  const shouldHaveBookingTime = isBooking && [LINE_ITEM_HOUR].includes(lineItemUnitType);
  const showBookingTimeForm = shouldHaveBookingTime && !isClosed && timeZone;

  const shouldHaveBookingDates =
    isBooking && [LINE_ITEM_DAY, LINE_ITEM_NIGHT].includes(lineItemUnitType);
  const showBookingDatesForm = shouldHaveBookingDates && !isClosed && timeZone;

  // The listing resource has a relationship: `currentStock`,
  // which you should include when making API calls.
  const isPurchase = isPurchaseProcess(processName);
  const currentStock = listing.currentStock?.attributes?.quantity;
  const isOutOfStock = isPurchase && lineItemUnitType === LINE_ITEM_ITEM && currentStock === 0;

  // Show form only when stock is fully loaded. This avoids "Out of stock" UI by
  // default before all data has been downloaded.
  const showProductOrderForm = isPurchase && typeof currentStock === 'number';

  const showInquiryForm = processName === INQUIRY_PROCESS_NAME;

  const supportedProcessesInfo = getSupportedProcessesInfo();
  const isKnownProcess = supportedProcessesInfo.map(info => info.name).includes(processName);

  const { pickupEnabled, shippingEnabled } = listing?.attributes?.publicData || {};

  const showClosedListingHelpText = listing.id && isClosed;
  const { formattedPrice, priceTitle } = priceData(price, marketplaceCurrency, intl);

  const subTitleText = showClosedListingHelpText
    ? intl.formatMessage({ id: 'OrderPanel.subTitleClosedListing' })
    : null;

  // Get location - prefer displayLocation (user's privacy choice), fall back to city/state/country
  const authorPublicData = author?.attributes?.profile?.publicData || {};
  const authorLocation = authorPublicData.location || {};
  const { city, country, state } = authorLocation;

  const countryCodes = getCountryCodes(EN);
  const countryName = countryCodes.find(c => c.code === country)?.name || country;

  // "City, State, Country": where the seller is (their display choice first)
  const formatLocation = () => {
    if (authorPublicData.displayLocation) {
      return [authorPublicData.displayLocation, countryName].filter(Boolean).join(', ');
    }
    if (!country) return null;
    return [city, state, countryName].filter(Boolean).join(', ');
  };
  const authorAddress = formatLocation();
  // Away on vacation: nobody can buy or make an offer (util/vacation.js).
  const sellerVacation = getSellerVacation(author);
  const classes = classNames(rootClassName || css.root, className);
  const titleClasses = classNames(titleClassName || css.orderTitle);
  const hasProfileImage = !!author?.profileImage?.id;

  return (
    <div className={classes}>
      <div className={css.orderHeading}>
        {/* What it is at a glance: sold, or its condition; the seller away */}
        <div className={css.badges}>
          {isOutOfStock ? (
            <span className={classNames(css.badge, css.badgeSold)}>
              <FormattedMessage id="OrderPanel.soldBadge" />
            </span>
          ) : (
            <span
              className={classNames(css.badge, {
                [css.badgeNew]: !condition || condition === 'new',
              })}
            >
              {conditionLabel(intl, condition || 'new') || capitalize(condition)}
            </span>
          )}
          {sellerVacation ? (
            <span className={classNames(css.badge, css.badgeAway)}>
              <FormattedMessage id="OrderPanel.awayBadge" />
            </span>
          ) : null}
        </div>
        <H2 className={titleClasses}>{title}</H2>
        <div className={css.author}>
          {hasProfileImage ? (
            <AvatarMedium user={author} className={css.providerAvatar} disableProfileLink />
          ) : null}
          <span className={css.providerNameLinked}>
            <FormattedMessage id="OrderPanel.soldBy" values={{ name: authorLink }} />
          </span>
          {authorAddress ? (
            <>
              <span className={css.authorDot} aria-hidden="true">
                ·
              </span>
              <span className={css.authorLocation}>{authorAddress}</span>
            </>
          ) : null}
        </div>
        {conditionDescription && condition !== 'new' ? (
          <p className={css.orderDescription}>
            <FormattedMessage
              id="OrderPanel.conditionNote"
              values={{ note: conditionDescription.trim() }}
            />
          </p>
        ) : null}
        {subTitleText ? <div className={css.orderHelp}>{subTitleText}</div> : null}
      </div>

      {/* The price: its own block, spaced by the panel's gap */}
      <PriceMaybe
        price={price}
        publicData={publicData}
        validListingTypes={validListingTypes}
        intl={intl}
      />

      {/* <div className={css.peopleLooking}>
          <Icons name="eye" />
          <span>
            <strong>32</strong> people are looking at this product
          </span>
        </div> */}

      {showPriceMissing ? (
        <PriceMissing />
      ) : showInvalidCurrency ? (
        <InvalidCurrency />
      ) : showBookingTimeForm ? (
        <BookingTimeForm
          className={css.bookingForm}
          formId="OrderPanelBookingTimeForm"
          lineItemUnitType={lineItemUnitType}
          onSubmit={onSubmit}
          price={price}
          marketplaceCurrency={marketplaceCurrency}
          dayCountAvailableForBooking={dayCountAvailableForBooking}
          listingId={listing.id}
          isOwnListing={isOwnListing}
          monthlyTimeSlots={monthlyTimeSlots}
          onFetchTimeSlots={onFetchTimeSlots}
          startDatePlaceholder={intl.formatDate(TODAY, dateFormattingOptions)}
          endDatePlaceholder={intl.formatDate(TODAY, dateFormattingOptions)}
          timeZone={timeZone}
          marketplaceName={marketplaceName}
          onFetchTransactionLineItems={onFetchTransactionLineItems}
          lineItems={lineItems}
          fetchLineItemsInProgress={fetchLineItemsInProgress}
          fetchLineItemsError={fetchLineItemsError}
        />
      ) : showBookingDatesForm ? (
        <BookingDatesForm
          className={css.bookingForm}
          formId="OrderPanelBookingDatesForm"
          lineItemUnitType={lineItemUnitType}
          onSubmit={onSubmit}
          price={price}
          marketplaceCurrency={marketplaceCurrency}
          dayCountAvailableForBooking={dayCountAvailableForBooking}
          listingId={listing.id}
          isOwnListing={isOwnListing}
          monthlyTimeSlots={monthlyTimeSlots}
          onFetchTimeSlots={onFetchTimeSlots}
          timeZone={timeZone}
          marketplaceName={marketplaceName}
          onFetchTransactionLineItems={onFetchTransactionLineItems}
          lineItems={lineItems}
          fetchLineItemsInProgress={fetchLineItemsInProgress}
          fetchLineItemsError={fetchLineItemsError}
        />
      ) : showProductOrderForm ? (
        <ProductOrderForm
          deliveryMethod={deliveryMethod}
          formId="OrderPanelProductOrderForm"
          onSubmit={onSubmit}
          price={price}
          marketplaceCurrency={marketplaceCurrency}
          currentStock={currentStock}
          pickupEnabled={pickupEnabled}
          shippingEnabled={shippingEnabled}
          listing={listing}
          listingId={listing.id}
          isOwnListing={isOwnListing}
          editListingParams={editListingParams}
          marketplaceName={marketplaceName}
          setIsSocialSharingModalOpen={setIsSocialSharingModalOpen}
          onOpenOffersModal={onOpenOffersModal}
          onContactUser={onContactUser}
          sellerVacation={sellerVacation}
        />
      ) : showInquiryForm ? (
        <InquiryWithoutPaymentForm formId="OrderPanelInquiryForm" onSubmit={onSubmit} />
      ) : !isKnownProcess ? (
        <p className={css.errorSidebar}>
          <FormattedMessage id="OrderPanel.unknownTransactionProcess" />
        </p>
      ) : null}
      <div className={css.openOrderForm}>
        <div className={css.priceContainerInCTA}>
          <div className={css.priceValue} title={priceTitle}>
            {formattedPrice}
          </div>
          <div className={css.perUnitInCTA}>
            <FormattedMessage id="OrderPanel.perUnit" values={{ unitType }} />
          </div>
        </div>

        {isClosed ? (
          <div className={css.closedListingButton}>
            <FormattedMessage id="OrderPanel.closedListingButtonText" />
          </div>
        ) : sellerVacation ? (
          <div className={css.closedListingButton}>
            <FormattedMessage id="OrderPanel.sellerAwayButtonText" />
          </div>
        ) : (
          <PrimaryButton
            onClick={handleSubmit(
              isOwnListing,
              isClosed,
              showInquiryForm,
              onSubmit,
              history,
              location
            )}
            disabled={isOutOfStock}
          >
            {isBooking ? (
              <FormattedMessage id="OrderPanel.ctaButtonMessageBooking" />
            ) : isOutOfStock ? (
              <FormattedMessage id="OrderPanel.ctaButtonMessageNoStock" />
            ) : isPurchase ? (
              <FormattedMessage id="OrderPanel.ctaButtonMessagePurchase" />
            ) : (
              <FormattedMessage id="OrderPanel.ctaButtonMessageInquiry" />
            )}
          </PrimaryButton>
        )}
      </div>
    </div>
  );
};

OrderPanel.defaultProps = {
  rootClassName: null,
  className: null,
  titleClassName: null,
  isOwnListing: false,
  authorLink: null,
  subTitle: null,
  monthlyTimeSlots: null,
  lineItems: null,
  fetchLineItemsError: null,
};

OrderPanel.propTypes = {
  rootClassName: string,
  className: string,
  titleClassName: string,
  listing: oneOfType([propTypes.listing, propTypes.ownListing]),
  validListingTypes: arrayOf(
    shape({
      listingType: string.isRequired,
      transactionType: shape({
        process: string.isRequired,
        alias: string.isRequired,
        unitType: string.isRequired,
      }).isRequired,
    })
  ).isRequired,
  isOwnListing: bool,
  author: oneOfType([propTypes.user, propTypes.currentUser]).isRequired,
  authorLink: node,
  onSubmit: func.isRequired,
  title: oneOfType([node, string]).isRequired,
  subTitle: oneOfType([node, string]),

  onFetchTimeSlots: func.isRequired,
  monthlyTimeSlots: object,
  onFetchTransactionLineItems: func.isRequired,
  onContactUser: func,
  lineItems: array,
  fetchLineItemsInProgress: bool.isRequired,
  fetchLineItemsError: propTypes.error,
  marketplaceCurrency: string.isRequired,
  dayCountAvailableForBooking: number.isRequired,
  marketplaceName: string.isRequired,

  // from withRouter
  history: shape({
    push: func.isRequired,
  }).isRequired,
  location: shape({
    search: string,
  }).isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

export default compose(
  withRouter,
  injectIntl
)(OrderPanel);
